import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import Nav from "../components/Nav";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { MaskedLines } from "../components/Reveal";

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            await register(form.email, form.password, form.name);
            toast.success("Account created.");
            navigate("/portal", { replace: true });
        } catch (e2) {
            const msg = formatApiErrorDetail(e2.response?.data?.detail) || e2.message;
            setErr(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background text-foreground">
            <Nav />
            <div className="mx-auto max-w-[1600px] px-6 md:px-10 pt-32 pb-24 grid grid-cols-12 gap-6">
                <div className="col-span-12 md:col-span-6">
                    <div className="text-[11px] tracking-[0.24em] uppercase text-muted-foreground mb-6 flex items-center gap-3">
                        <span className="w-8 h-px bg-muted-foreground" />
                        Enrollment
                    </div>
                    <MaskedLines
                        as="h1"
                        className="font-editorial text-6xl md:text-8xl tracking-tighter leading-[0.9]"
                        lines={["Begin your", <><span className="italic text-accent">file.</span></>]}
                    />
                    <p className="mt-8 text-muted-foreground max-w-md text-base leading-relaxed">
                        Enrollment takes under a minute. Your portal is provisioned instantly with a
                        sample of what your dashboard will look like as work begins.
                    </p>
                </div>

                <form
                    onSubmit={submit}
                    className="col-span-12 md:col-span-5 md:col-start-8 bg-background border hairline p-8 md:p-12"
                    data-testid="register-form"
                >
                    <div className="space-y-8">
                        <label className="block">
                            <span className="block text-[11px] tracking-[0.24em] uppercase text-muted-foreground mb-3">Full name</span>
                            <input
                                required
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                data-testid="register-name"
                                className="w-full bg-transparent border-b hairline focus:border-foreground outline-none py-3 font-mono-ui text-sm"
                            />
                        </label>
                        <label className="block">
                            <span className="block text-[11px] tracking-[0.24em] uppercase text-muted-foreground mb-3">Email</span>
                            <input
                                type="email"
                                required
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                data-testid="register-email"
                                className="w-full bg-transparent border-b hairline focus:border-foreground outline-none py-3 font-mono-ui text-sm"
                            />
                        </label>
                        <label className="block">
                            <span className="block text-[11px] tracking-[0.24em] uppercase text-muted-foreground mb-3">Password</span>
                            <input
                                type="password"
                                required
                                minLength={6}
                                value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                                data-testid="register-password"
                                className="w-full bg-transparent border-b hairline focus:border-foreground outline-none py-3 font-mono-ui text-sm"
                            />
                        </label>
                        {err && (
                            <div data-testid="register-error" className="text-sm text-destructive">
                                {err}
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="register-submit"
                            className="group w-full inline-flex items-center justify-between gap-3 bg-foreground text-background px-6 py-4 text-[13px] tracking-[0.16em] uppercase hover:bg-accent disabled:opacity-60"
                        >
                            {loading ? "Creating account…" : "Create account"}
                            <ArrowUpRight size={16} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                        </button>
                        <div className="text-xs font-mono-ui text-muted-foreground">
                            Already enrolled?{" "}
                            <Link to="/login" className="underline underline-offset-4 text-foreground">
                                Sign in
                            </Link>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}
